/**
 * Finding a recipe in a list — the recipe page's search box, its tag chips,
 * the sort control, and ⌘K all share this so a tag that finds a recipe in one
 * place finds it in the others.
 *
 * Matching follows the catalog type-ahead: case- and accent-insensitive prefix
 * matching, AND across query tokens. The haystack is the name, the BJCP style
 * (name and code), and the user-defined tags — the three things a brewer
 * actually types when they are looking for "the house pale". Notes stay out of
 * it; a process aside that happens to mention "lager" should not hide the ales.
 */

import { getStyle, normalize, styleCode } from '@beerwright/catalog';
import type { JournalDoc, RecipeDoc } from '@beerwright/commands';
import { naturalCompare } from '@/lib/natural-sort';

/** Split a string into the tokens {@link recipeMatchesQuery} compares. */
function tokensOf(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/** Name, style, and tags — everything a recipe can be found by. */
function searchParts(recipe: RecipeDoc): readonly string[] {
  const style = recipe.styleId === null ? undefined : getStyle(recipe.styleId);
  const parts: string[] = [recipe.name, ...recipe.tags];
  if (style !== undefined) parts.push(style.name, styleCode(style));
  return parts;
}

/**
 * True when every query token prefixes some token in the recipe's name, style,
 * or tags. An empty (or whitespace-only) query matches everything — tag chips
 * still narrow on their own.
 */
export function recipeMatchesQuery(recipe: RecipeDoc, query: string): boolean {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) return true;
  const haystack = searchParts(recipe).flatMap(tokensOf);
  return queryTokens.every((token) => haystack.some((part) => part.startsWith(token)));
}

/** True when the recipe carries every selected tag (AND). */
export function recipeHasTags(recipe: RecipeDoc, tags: readonly string[]): boolean {
  if (tags.length === 0) return true;
  return tags.every((tag) => recipe.tags.includes(tag));
}

/** Recipes matching a free-text query and an optional set of required tags. */
export function filterRecipes(
  recipes: readonly RecipeDoc[],
  options: { readonly query?: string; readonly tags?: readonly string[] } = {},
): readonly RecipeDoc[] {
  const query = options.query ?? '';
  const tags = options.tags ?? [];
  return recipes.filter(
    (recipe) => recipeMatchesQuery(recipe, query) && recipeHasTags(recipe, tags),
  );
}

/**
 * The distinct tags across every document that carries them, sorted for a
 * stable chip row.
 *
 * One vocabulary across recipes and journals (D-M4): a brewer who tagged a
 * recipe "saison" is offered "saison" on the journal about the batch they
 * brewed from it, and the other way round. Two vocabularies would be two
 * spellings of the same word within a week.
 */
export function collectTags(
  recipes: readonly RecipeDoc[],
  journals: readonly JournalDoc[] = [],
): readonly string[] {
  const seen = new Set<string>();
  for (const recipe of recipes) {
    for (const tag of recipe.tags) seen.add(tag);
  }
  for (const journal of journals) {
    for (const tag of journal.tags) seen.add(tag);
  }
  return [...seen].sort(naturalCompare);
}

/** The distinct tags in a recipe list. @see collectTags */
export function collectRecipeTags(recipes: readonly RecipeDoc[]): readonly string[] {
  return collectTags(recipes);
}

/** A capped tag row: the chips it shows, and the tags it holds back. */
export interface VisibleTags {
  readonly shown: readonly string[];
  readonly hidden: readonly string[];
}

/**
 * The first `limit` tags of a row — except that an active tag is never the one
 * hidden.
 *
 * A tag the brewer is filtering on has to stay on screen: the row would
 * otherwise be narrowed by a chip it does not show, with nothing to press to
 * undo it. So an active tag past the cutoff is appended and the last inactive
 * tag makes way for it, keeping the count at `limit`. When every slot is
 * already active the row runs past the limit rather than drop one.
 */
export function visibleTags(
  tags: readonly string[],
  active: readonly string[],
  limit: number,
  expanded: boolean,
): VisibleTags {
  if (expanded) return { shown: [...tags], hidden: [] };
  const shown = tags.slice(0, limit);
  for (const tag of tags.slice(limit)) {
    if (!active.includes(tag)) continue;
    for (let index = shown.length - 1; index >= 0; index--) {
      const candidate = shown[index];
      if (candidate === undefined || active.includes(candidate)) continue;
      shown.splice(index, 1);
      break;
    }
    shown.push(tag);
  }
  const visible = new Set(shown);
  return { shown, hidden: tags.filter((tag) => !visible.has(tag)) };
}

/**
 * cmdk's searchable string for a recipe. Tags and style have to live in here
 * (not just on screen) or typing them in ⌘K matches nothing.
 */
export function recipeCommandValue(recipe: RecipeDoc): string {
  return ['recipe', ...searchParts(recipe)].join(' ');
}

/** Extra cmdk keywords — the same fields as {@link recipeCommandValue}. */
export function recipeCommandKeywords(recipe: RecipeDoc): readonly string[] {
  return searchParts(recipe).slice(1);
}

/** How the recipe list can be ordered. Default is last updated — newest edit first. */
export const RECIPE_SORTS = ['updated', 'name', 'style'] as const;

/** @see RECIPE_SORTS */
export type RecipeSort = (typeof RECIPE_SORTS)[number];

/** Labels for the sort control. */
export const RECIPE_SORT_LABELS: Readonly<Record<RecipeSort, string>> = {
  updated: 'Last updated',
  name: 'Name',
  style: 'Style',
};

/**
 * A recipe's position in the BJCP guidelines: the numbered categories in
 * order, then the provisional X series, then everything unplaceable.
 *
 * `null` when the recipe declares no style, or names one the catalog does not
 * know — both sort last of all, after the provisionals.
 *
 * Provisional styles (X1, X2, …) carry a null `categoryLetter` and reuse the
 * numbers 1 upward, so they cannot be interleaved with the categories: X1
 * would land beside category 1, which is nowhere a brewer would look for it.
 * They sort as a block after category 34 — where the guidelines put them, and
 * where the style picker renders them — ordered among themselves by X number.
 *
 * Sorting by the style's *name* is what this used to do, and it is not what
 * "sort by style" means to a brewer: it interleaved the guidelines
 * alphabetically, so 21A American IPA filed under A, miles from 21B Specialty
 * IPA under S. Ordering by the code keeps a category together, which is the
 * order the guidelines are written in and the order the list's own docstring
 * always claimed.
 *
 * The number is compared as a number rather than through the rendered `21A`
 * string, so there is no digit-run parsing to get wrong — category 2 lands
 * before category 10 by arithmetic.
 */
function styleSortKey(
  recipe: RecipeDoc,
): { readonly provisional: boolean; readonly number: number; readonly letter: string } | null {
  if (recipe.styleId === null) return null;
  const style = getStyle(recipe.styleId);
  if (style === undefined) return null;
  return {
    provisional: style.categoryLetter === null,
    number: style.categoryNumber,
    letter: style.categoryLetter ?? '',
  };
}

const COMPARE: Readonly<Record<RecipeSort, (a: RecipeDoc, b: RecipeDoc) => number>> = {
  updated: (a, b) => b.updatedAt - a.updatedAt || b.createdAt - a.createdAt,
  name: (a, b) => naturalCompare(a.name, b.name) || b.updatedAt - a.updatedAt,
  style: (a, b) => {
    const left = styleSortKey(a);
    const right = styleSortKey(b);
    if (left === null && right === null) return naturalCompare(a.name, b.name);
    if (left === null) return 1;
    if (right === null) return -1;
    return (
      Number(left.provisional) - Number(right.provisional) ||
      left.number - right.number ||
      naturalCompare(left.letter, right.letter) ||
      naturalCompare(a.name, b.name)
    );
  },
};

/**
 * Order a recipe list.
 *
 * `updated` is the list's default: the recipe you just edited stays at the
 * top. Name and style exist so a shelf of a dozen house beers can be scanned
 * alphabetically or in BJCP guideline order — see {@link styleSortKey}, which
 * orders by category number and letter rather than by the style's name.
 */
export function sortRecipes(recipes: readonly RecipeDoc[], sort: RecipeSort): readonly RecipeDoc[] {
  return [...recipes].sort(COMPARE[sort]);
}
